import { ApiClientError, type ProjectResponse } from '@contract-analyzer/api-client';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowLeftIcon } from 'lucide-react';
import { NavLink, Outlet, useParams } from 'react-router';

import { FullPageState, PageLoadingState } from '@/components/common';
import { Button } from '@/components/ui/button';
import { ProjectCtx, type ProjectContext } from '@/hooks/project-context';
import { useTenant } from '@/hooks/use-tenant';
import { apiClient } from '@/lib/api';
import { tenantPath } from '@/lib/routes';

type ProjectLoadState = 'loading' | 'ready' | 'not_found' | 'error';

interface ProjectResourceState {
  readonly error: string | null;
  readonly project: ProjectResponse | null;
  readonly resolvedProjectId: string | null;
  readonly resolvedTenantId: string | null;
  readonly status: ProjectLoadState;
}

function ProjectLoadingView() {
  return <PageLoadingState variant="compact" label="Loading project…" />;
}

function ProjectErrorView({ message }: { readonly message: string }) {
  return <FullPageState title="Something went wrong" message={message} tone="error" />;
}

function ProjectNotFoundState({ tenantSlug }: { readonly tenantSlug: string }) {
  return (
    <FullPageState
      title="Project not found"
      message="The project may have been archived or you may not have access."
      actions={
        <Button asChild variant="outline">
          <NavLink to={tenantPath(tenantSlug, 'projects')}>
            <ArrowLeftIcon data-icon="inline-start" />
            Back to projects
          </NavLink>
        </Button>
      }
    />
  );
}

export function ProjectProvider() {
  const { projectId } = useParams<{ projectId: string }>();
  const tenant = useTenant();
  const [resource, setResource] = useState<ProjectResourceState>({
    error: null,
    project: null,
    resolvedProjectId: null,
    resolvedTenantId: null,
    status: 'loading',
  });

  useEffect(() => {
    if (projectId === undefined) {
      return;
    }

    let cancelled = false;

    void apiClient
      .getProject(projectId)
      .then((nextProject) => {
        if (cancelled) {
          return;
        }

        if (nextProject.tenant_id !== tenant.tenantId) {
          setResource({
            error: null,
            project: null,
            resolvedProjectId: projectId,
            resolvedTenantId: tenant.tenantId,
            status: 'not_found',
          });
          return;
        }

        setResource({
          error: null,
          project: nextProject,
          resolvedProjectId: projectId,
          resolvedTenantId: tenant.tenantId,
          status: 'ready',
        });
      })
      .catch((cause: unknown) => {
        if (cancelled) {
          return;
        }

        if (cause instanceof ApiClientError && cause.status === 404) {
          setResource({
            error: null,
            project: null,
            resolvedProjectId: projectId,
            resolvedTenantId: tenant.tenantId,
            status: 'not_found',
          });
          return;
        }

        setResource({
          error:
            cause instanceof Error && cause.message.trim().length > 0
              ? cause.message
              : 'Failed to load project.',
          project: null,
          resolvedProjectId: projectId,
          resolvedTenantId: tenant.tenantId,
          status: 'error',
        });
      });

    return () => {
      cancelled = true;
    };
  }, [projectId, tenant.tenantId]);

  const isCurrentRequestResolved =
    resource.resolvedProjectId === projectId && resource.resolvedTenantId === tenant.tenantId;

  const updateProject = useCallback((nextProject: ProjectResponse) => {
    setResource((current) => {
      if (current.project?.project_id !== nextProject.project_id) {
        return current;
      }

      return {
        ...current,
        project: nextProject,
      };
    });
  }, []);

  const context = useMemo<ProjectContext | null>(() => {
    if (resource.project === null) {
      return null;
    }

    return { project: resource.project, updateProject };
  }, [resource.project, updateProject]);

  if (projectId === undefined) {
    return <ProjectErrorView message="No project selected." />;
  }

  if (!isCurrentRequestResolved || resource.status === 'loading') {
    return <ProjectLoadingView />;
  }

  if (resource.status === 'not_found') {
    return <ProjectNotFoundState tenantSlug={tenant.slug} />;
  }

  if (resource.status === 'error' || context === null) {
    return <ProjectErrorView message={resource.error ?? 'Failed to load project.'} />;
  }

  return (
    <ProjectCtx value={context}>
      <Outlet />
    </ProjectCtx>
  );
}
